import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export interface ColumnAliasModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  columnId: string;
  originalTitle: string;
  originalDescription?: string;
  currentAlias?: string;
  onSave: (columnId: string, alias: string) => void;
  onClear?: (columnId: string) => void;
}

export function ColumnAliasModal({
  open,
  onOpenChange,
  columnId,
  originalTitle,
  originalDescription,
  currentAlias,
  onSave,
  onClear,
}: ColumnAliasModalProps) {
  const [aliasValue, setAliasValue] = useState(currentAlias || "");
  const [isValid, setIsValid] = useState(true);

  const isEditing = Boolean(currentAlias);

  // Reset form when modal opens/closes or column changes
  useEffect(() => {
    if (open) {
      setAliasValue(currentAlias || "");
      setIsValid(true);
    }
  }, [open, currentAlias]);

  const handleSave = () => {
    const trimmedAlias = aliasValue.trim();

    if (!trimmedAlias) {
      setIsValid(false);
      return;
    }

    onSave(columnId, trimmedAlias);
    onOpenChange(false);
  };

  const handleClear = () => {
    if (onClear && currentAlias) {
      onClear(columnId);
      onOpenChange(false);
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  const handleInputChange = (value: string) => {
    setAliasValue(value);
    // Clear validation error when user starts typing
    if (!isValid && value.trim()) {
      setIsValid(true);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] bg-card">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? "Edit Display Alias" : "Set Display Alias"}
          </DialogTitle>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {/* Metric Information Card */}
          <Card className="bg-muted/30">
            <CardContent className="pt-4">
              <div className="space-y-2">
                <h4 className="font-medium text-sm">{originalTitle}</h4>
                {originalDescription && (
                  <p className="text-sm text-muted-foreground">
                    {originalDescription}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Display Alias Input */}
          <div className="grid gap-2">
            <Label htmlFor="display-alias" className="text-sm font-medium">
              Display Alias
            </Label>
            <Input
              id="display-alias"
              placeholder="Enter custom display name..."
              value={aliasValue}
              onChange={(e) => handleInputChange(e.target.value)}
              className={!isValid ? "border-destructive" : ""}
              autoFocus
            />
            {!isValid && (
              <p className="text-sm text-destructive">
                Display alias cannot be empty
              </p>
            )}
          </div>
        </div>

        <DialogFooter className="flex gap-2">
          {/* Clear Button - only show when editing */}
          {isEditing && onClear && (
            <Button
              type="button"
              variant="outline"
              onClick={handleClear}
              className="mr-auto"
            >
              Clear
            </Button>
          )}

          {/* Cancel Button */}
          <Button type="button" variant="outline" onClick={handleCancel}>
            Cancel
          </Button>

          {/* Save Button */}
          <Button
            type="button"
            onClick={handleSave}
            disabled={!aliasValue.trim()}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
